import React, {Component} from 'react';
import {NavLink, withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import {getCategoryBenefits, setDynamicLink, setSearchFilters} from '../../redux/actions';
import * as routes from '../../constants/routes';
import {
    cleanUpObject,
    clearSearchFilters,
    combineCategoryRelevantBenefitTypes,
    createPayloadFromSearchFilters,
    findCategoryById,
    getMediaPath,
    parseApiResponse,
    parseSearchFiltersFromUrl,
} from '../../app/functions';
import BreadCrumbs from '../../components/common/bread_crumbs';
import Loader from '../../components/common/loader';
import Page404 from '../page404';
import TopBanner from './top_banner';
import Filters from './filters';
import Results from './results';
import './index.scss';

class Category extends Component {
    constructor(props) {
        super(props);

        this.state = {
            currentCategory: undefined,
            availableCategories: [],
            availableTags: [],
            tagsDefaultVisibility: false,
            shouldRequestData: false,
            waitingForInitialResponse: false,
            initializationCompleted: false,
        };

        this.initializeComponentState = this.initializeComponentState.bind(this);
        this.afterResponseFromAPI = this.afterResponseFromAPI.bind(this);
        this.getBenefits = this.getBenefits.bind(this);
        this.getBreadCrumbs = this.getBreadCrumbs.bind(this);
        this.getAvailableCategories = this.getAvailableCategories.bind(this);
        this.getAvailableTags = this.getAvailableTags.bind(this);
    }

    componentDidMount() {
        const {history, categoryBenefits} = this.props;

        let shouldRequestData = false;

        // If we get to the page by push action - get benefits
        if (history.action === 'PUSH') {
            shouldRequestData = true;
        }

        // If user clicked back but no benefits - get benefits
        if (history.action === 'POP' && categoryBenefits === false) {
            shouldRequestData = true;
        }

        this.initializeComponentState(shouldRequestData);
    }

    getBannerLink() {
        const linkConfig = this.state.currentCategory?.banner_link || {};
        const result = {
            type: linkConfig.type,
            route: linkConfig.route
        }

        if (typeof linkConfig === 'string') {
            result.route = this.state.currentCategory?.banner_link;
            result.type = 'external'
        }

        if (result.type === 'internal') {
            result.route = routes[result.route]
            if (linkConfig.parameters.id)
                result.route = result.route + '/' + linkConfig.parameters.id
        }

        return result
    }

    getBanner(cat, img) {
        const link = this.getBannerLink()

        const image = <img
            src={getMediaPath(img)}
            alt={cat.name}
            className="category-main-banner"
        />

        if (!link || !link.route)
            return image

        if (link.type === 'internal')
            return <NavLink to={link.route}> {image} </NavLink>

        return <a href={link.route}> {image} </a>
    }

    componentWillUnmount() {
        const {searchFilters, setSearchFilters, resetDynamicLink} = this.props;

        setSearchFilters(
            clearSearchFilters(searchFilters)
        );
        resetDynamicLink();
    }

    componentDidUpdate(prevProps) {
        const {
            match,
            searchFilters,
            pageNum,
            categoryBenefits,
        } = this.props;

        let {shouldRequestData} = this.state;
        let shouldUpdateTags = false;

        // If the route changes, go through initialization again
        if (match.params.cat_id !== prevProps.match.params.cat_id) {
            this.initializeComponentState(true);
        }

        // If there's new response from the API, update relevant state
        if (categoryBenefits !== prevProps.categoryBenefits) {
            let currentCategory, mainCategory;

            if (searchFilters.subCategory.id !== -1) {
                currentCategory = findCategoryById(this.props.categories, searchFilters.subCategory.id);
                mainCategory = findCategoryById(this.props.categories, searchFilters.category.id);
            } else {
                mainCategory = findCategoryById(this.props.categories, searchFilters.category.id);
            }

            if (!currentCategory?.bannerDesktopMediaPath && !currentCategory?.bannerMobileMediaPath) {
                this.setState({
                    ...this.state,
                    currentCategory: {
                        ...this.state.currentCategory,
                        bannerMobileMediaPath: mainCategory?.bannerMobileMediaPath,
                        bannerDesktopMediaPath: mainCategory?.bannerDesktopMediaPath,
                        banner_link: mainCategory?.banner_link,
                    }
                });
            } else if (currentCategory.bannerDesktopMediaPath || currentCategory.bannerMobileMediaPath) {
                this.setState({
                    ...this.state,
                    currentCategory: {
                        ...this.state.currentCategory,
                        bannerMobileMediaPath: currentCategory?.bannerMobileMediaPath,
                        bannerDesktopMediaPath: currentCategory?.bannerDesktopMediaPath,
                        banner_link: currentCategory?.banner_link,
                    }
                });
            }

            this.afterResponseFromAPI();
        }

        // Send new request to the API if the filters/pageNum change
        if (searchFilters !== prevProps.searchFilters || pageNum !== prevProps.pageNum) {
            shouldRequestData = true;

            // If the sub-category was changed, update availableTags as well
            if (searchFilters.subCategory !== prevProps.searchFilters.subCategory) {
                shouldUpdateTags = true;


            }
        }

        if (shouldRequestData) {
            this.getBenefits(shouldUpdateTags);
        }
    }

    initializeComponentState(shouldRequestData) {
        const {
            match,
            categories,
            searchFilters,
            setSearchFilters,
            setDynamicLink,
        } = this.props;

        const currentCategory = findCategoryById(categories, match.params.cat_id);

        setDynamicLink(currentCategory?.dynamicLink != null ? currentCategory.dynamicLink : false);

        const newFilters = parseSearchFiltersFromUrl(
            clearSearchFilters(searchFilters)
        );

        // Set category ID from route params
        newFilters.category.id = match.params.cat_id;

        setSearchFilters(newFilters);

        this.setState({
            ...this.state,
            currentCategory,
            availableCategories: this.getAvailableCategories(newFilters),
            availableTags: this.getAvailableTags(newFilters),
            tagsDefaultVisibility: (newFilters.benefitType.id !== -1),
            shouldRequestData,
            waitingForInitialResponse: shouldRequestData,
            initializationCompleted: true,
        });
    }

    afterResponseFromAPI() {
        this.setState({
            waitingForInitialResponse: false,
        });
    }

    getBenefits(shouldUpdateTags) {
        const {searchFilters, pageNum, getCategoryBenefits, match} = this.props;

        const requestPayload = createPayloadFromSearchFilters(searchFilters, pageNum);
        getCategoryBenefits(requestPayload);

        const newState = {
            ...this.state,
            shouldRequestData: false,
        };

        if (shouldUpdateTags) {
            newState.availableTags = this.getAvailableTags(searchFilters);
        }
        this.setState(newState);
    }

    getBreadCrumbs() {
        const {searchFilters} = this.props;
        const {currentCategory} = this.state;

        const crumbs = [
            {
                text: currentCategory.name,
                route: `${routes.category}/${currentCategory.id}/${currentCategory.url_name}`,
            },
        ];

        // If there's a selected sub-category, add it also to crumbs
        if (searchFilters.subCategory.id !== -1) {
            const selectedSubCategory = findCategoryById(currentCategory.subCategories, searchFilters.subCategory.id);

            if (selectedSubCategory) {
                crumbs.push({
                    text: selectedSubCategory.name,
                });
            }
        }

        return crumbs;
    }

    getAvailableCategories(searchFilters) {
        const {translations, categories} = this.props;

        const categoriesResult = [
            {
                id: -1,
                title: translations['category_all_subcategories'],
            },
        ];

        const currentCategory = findCategoryById(categories, searchFilters.category.id);

        if (!currentCategory) {
            return categoriesResult;
        }

        categoriesResult.push(
            ...parseApiResponse(
                currentCategory.subCategories, {id: 'number'}
            )
        );

        return categoriesResult;
    }

    getAvailableTags(searchFilters) {
        const {categories, benefitTypes} = this.props;

        const tagResults = [];

        const currentCategory = findCategoryById(categories, searchFilters.category.id);

        if (!currentCategory) {
            return tagResults;
        }

        // By default, show the benefit types of current category
        let relevantBenefitTypes = combineCategoryRelevantBenefitTypes(currentCategory);

        // If there's a selected sub-category, show its benefit types instead
        if (searchFilters.subCategory.id !== -1) {
            const selectedSubCategory = findCategoryById(categories, searchFilters.subCategory.id);

            if (selectedSubCategory) {
                relevantBenefitTypes = combineCategoryRelevantBenefitTypes(selectedSubCategory);
            }
        }

        tagResults.push(
            ...parseApiResponse(
                Object.values(benefitTypes), {id: 'number'}
            ).filter(
                (tagData) => relevantBenefitTypes.includes(tagData.id)
            )
        );

        return tagResults;
    }

    render() {
        const {deviceState, categoryBenefits} = this.props;
        const {
            currentCategory,
            availableCategories,
            availableTags,
            tagsDefaultVisibility,
            waitingForInitialResponse,
            initializationCompleted,
        } = this.state;

        const isMobile = (deviceState === 'mobile');
        const bannerPath = isMobile
            ? currentCategory?.bannerMobileMediaPath
            : currentCategory?.bannerDesktopMediaPath;
        return (
            <main className="category-page">
                {currentCategory ? (
                    <>
                        <TopBanner
                            categoryName={currentCategory.name}
                        />

                        <BreadCrumbs
                            root={{
                                text: 'דף הבית',
                                route: routes.home,
                            }}
                            crumbs={this.getBreadCrumbs()}
                            className="category-breadcrumbs"
                        />

                        <div className="content-container">
                            {categoryBenefits && !waitingForInitialResponse ? (
                                <>
                                    {bannerPath && this.getBanner(currentCategory, bannerPath)}

                                    <Filters
                                        currentCategory={currentCategory}
                                        availableCategories={availableCategories}
                                        availableTags={availableTags}
                                        tagsDefaultVisibility={tagsDefaultVisibility}
                                    />

                                    <Results data={categoryBenefits}/>
                                </>
                            ) : (
                                <Loader className="data-loader"/>
                            )}
                        </div>
                    </>
                ) : (
                    initializationCompleted && (
                        <Page404/>
                    )
                )}
            </main>
        );
    }
}

const mapStateToProps = (store) => {
    return {
        translations: store.config.Translations,
        categories: cleanUpObject(store.config.categoriesArr).Data,
        benefitTypes: cleanUpObject(store.config.BenefitTypes),
        deviceState: store.state.deviceState,
        searchFilters: store.state.benefitSearchFilters,
        pageNum: store.state.searchPageNum,
        categoryBenefits: store.data.categoryBenefits,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setDynamicLink: (payload) => dispatch(setDynamicLink(payload)),
        resetDynamicLink: () => dispatch(setDynamicLink(false)),
        setSearchFilters: (payload) => dispatch(setSearchFilters(payload)),
        getCategoryBenefits: (payload) => dispatch(getCategoryBenefits(payload)),
    };
};

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps, null, {pure: false})(Category)
);
